var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getModuleIdFromWidget, loadScriptFileAsync, lookupComponent, hasValue, normalizeComponentId, normalizeModuleScriptUrl } from '@utils';
/**
 * Generate a unique widget instance ID
 */
export function generateWidgetInstanceId() {
    return `widget-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}
/**
 * Check if a widget position collides with any existing widgets
 *
 * @param x - X position to check
 * @param y - Y position to check
 * @param width - Width of the widget
 * @param height - Height of the widget
 * @param widgets - Existing widgets on the dashboard
 * @returns True if position is free (no collision), false otherwise
 */
function isPositionFree(x, y, width, height, widgets) {
    // Check if widget goes out of bounds
    if (x < 0 || y < 0 || width <= 0 || height <= 0) {
        return false;
    }
    // Check collision with each existing widget
    for (const widget of widgets) {
        const layout = widget.layout;
        // Check if rectangles overlap
        // Two rectangles overlap if they overlap in both x and y dimensions
        const xOverlap = x < layout.x + layout.w && x + width > layout.x;
        const yOverlap = y < layout.y + layout.h && y + height > layout.y;
        if (xOverlap && yOverlap) {
            return false; // Collision detected
        }
    }
    return true; // No collision
}
/**
 * Find the optimal position for a new widget on the dashboard
 * Comprehensively scans each row level from top to bottom to find the topmost available position
 *
 * @param widgets - Existing widgets on the dashboard
 * @param widgetWidth - Width of the new widget
 * @param widgetHeight - Height of the new widget
 * @param direction - Text direction (ltr/rtl)
 * @param restrictedMode - If true (isBounded), place at top-left (0, 0) for user to reposition
 * @param maxColumns - Maximum number of columns in the grid (default: 40)
 * @returns Position coordinates {x, y}
 */
export function findNextLayoutPosition(widgets, widgetWidth, widgetHeight, direction = 'ltr', restrictedMode = false, maxColumns = 40, orientation) {
    // In restricted mode (isBounded), place at center so the widget appears in the
    // middle of the bounded area. User can then drag to desired position.
    if (restrictedMode) {
        const centeredX = Math.max(0, Math.floor((maxColumns - widgetWidth) / 2));
        return { x: centeredX, y: 0 };
    }
    // Vertical orientation: always place at the bottom of the stack
    if (orientation === 'vertical') {
        const maxBottom = widgets.reduce((max, w) => Math.max(max, w.layout.y + w.layout.h), 0);
        return { x: 0, y: maxBottom };
    }
    // Horizontal orientation: always place at the right edge of the strip
    if (orientation === 'horizontal') {
        const maxRight = widgets.reduce((max, w) => Math.max(max, w.layout.x + w.layout.w), 0);
        return { x: maxRight, y: 0 };
    }
    // Handle empty dashboard case
    if (widgets.length === 0) {
        if (direction === 'rtl') {
            return { x: maxColumns - widgetWidth, y: 0 };
        }
        return { x: 0, y: 0 };
    }
    // Collect all unique Y positions from existing widgets
    const yPositions = new Set();
    yPositions.add(0); // Always check from the top
    widgets.forEach((widget) => {
        yPositions.add(widget.layout.y); // Top edge of widget
        yPositions.add(widget.layout.y + widget.layout.h); // Bottom edge of widget
    });
    // Sort Y positions ascending (top to bottom)
    const sortedYPositions = Array.from(yPositions).sort((a, b) => a - b);
    // For each Y level, scan all X positions
    for (const y of sortedYPositions) {
        if (direction === 'rtl') {
            // RTL: Scan from right to left
            for (let x = maxColumns - widgetWidth; x >= 0; x--) {
                if (isPositionFree(x, y, widgetWidth, widgetHeight, widgets)) {
                    return { x, y };
                }
            }
        }
        else {
            // LTR: Scan from left to right
            for (let x = 0; x <= maxColumns - widgetWidth; x++) {
                if (isPositionFree(x, y, widgetWidth, widgetHeight, widgets)) {
                    return { x, y };
                }
            }
        }
    }
    // Fallback: Place at the bottom (should rarely reach here)
    const maxY = Math.max(...widgets.map(w => w.layout.y + w.layout.h));
    const fallbackX = direction === 'rtl' ? maxColumns - widgetWidth : 0;
    return { x: fallbackX, y: maxY };
}
/**
 * Create widget instances from selected widgets with proper positioning
 *
 * @param selectedWidgets - Widgets selected from the drawer
 * @param existingWidgets - Currently existing widgets on dashboard
 * @param options - Creation options (context, direction, restrictedMode)
 * @returns Array of new widget instances ready to be added
 */
export function createWidgetInstances(selectedWidgets, existingWidgets, options) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const { context, direction, restrictedMode = false, maxColumns = 40, orientation } = options;
        const newInstances = [];
        // Track widgets as we add them for proper positioning
        let currentWidgets = [...existingWidgets];
        for (const selectedWidget of selectedWidgets) {
            // Check if widget is already registered
            let registeredInstance = lookupComponent(selectedWidget.id);
            // If not registered, load the script
            if (!registeredInstance) {
                const moduleId = getModuleIdFromWidget(selectedWidget);
                const rawUrl = selectedWidget.sourceUrl;
                const scriptUrl = rawUrl ? normalizeModuleScriptUrl(rawUrl) : null;
                if (scriptUrl) {
                    try {
                        yield loadScriptFileAsync(scriptUrl, moduleId);
                        // Check again after loading
                        registeredInstance = lookupComponent(selectedWidget.id);
                    }
                    catch (error) {
                        console.error('Failed to load widget script:', error);
                        continue;
                    }
                }
            }
            if (registeredInstance) {
                // Merge registered widget with selected widget details — component comes from registry
                const mergedWidget = Object.assign(Object.assign({}, selectedWidget), { component: registeredInstance.component, configs: (_a = registeredInstance.configs) !== null && _a !== void 0 ? _a : selectedWidget.configs });
                const widgetInstance = createSingleWidgetInstance(mergedWidget, currentWidgets, direction, restrictedMode, maxColumns, orientation);
                newInstances.push(widgetInstance);
                // Add to current widgets for next position calculation
                currentWidgets.push(widgetInstance);
            }
        }
        return newInstances;
    });
}
/**
 * Create a single widget instance with proper layout
 *
 * @param widget - Widget to create instance from
 * @param existingWidgets - Existing widgets for position calculation
 * @param direction - Text direction
 * @param restrictedMode - If true, use top-left positioning for bounded dashboards
 * @param maxColumns - Maximum number of columns in the grid
 * @returns New widget instance
 */
function createSingleWidgetInstance(widget, existingWidgets, direction, restrictedMode = false, maxColumns = 40, orientation) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    // Generate unique instance ID
    const instanceId = generateWidgetInstanceId();
    const defaultWidth = (_f = (_c = (_b = (_a = widget.configs) === null || _a === void 0 ? void 0 : _a.layout) === null || _b === void 0 ? void 0 : _b.w) !== null && _c !== void 0 ? _c : (_e = (_d = widget.configs) === null || _d === void 0 ? void 0 : _d.layout) === null || _e === void 0 ? void 0 : _e.minW) !== null && _f !== void 0 ? _f : 10;
    const defaultHeight = (_m = (_j = (_h = (_g = widget.configs) === null || _g === void 0 ? void 0 : _g.layout) === null || _h === void 0 ? void 0 : _h.h) !== null && _j !== void 0 ? _j : (_l = (_k = widget.configs) === null || _k === void 0 ? void 0 : _k.layout) === null || _l === void 0 ? void 0 : _l.minH) !== null && _m !== void 0 ? _m : 10;
    // Find optimal position
    const position = findNextLayoutPosition(existingWidgets, defaultWidth, defaultHeight, direction, restrictedMode, maxColumns, orientation);
    return {
        _id: instanceId,
        id: widget.id,
        key: instanceId,
        name: widget.name,
        component: widget.component || widget.widget,
        configs: widget.configs,
        defaultProps: widget.defaultProps,
        configuredProps: {},
        layout: {
            i: instanceId,
            x: position.x,
            y: position.y,
            w: defaultWidth,
            h: defaultHeight,
            static: false,
            _id: instanceId
        },
        hasConfigured: false,
        direction: direction,
        sourceUrl: widget.sourceUrl,
        usecaseId: widget.usecaseId,
    };
}
/**
 * Load a widget's script dynamically
 *
 * @param widget - Widget to load script for
 * @returns The registered widget or null if failed
 */
export function loadWidgetScript(widget) {
    return __awaiter(this, void 0, void 0, function* () {
        let registeredInstance = lookupComponent(widget.id);
        if (registeredInstance) {
            return registeredInstance;
        }
        const moduleId = getModuleIdFromWidget(widget);
        const rawUrl = widget.sourceUrl;
        const scriptUrl = rawUrl ? normalizeModuleScriptUrl(rawUrl) : null;
        if (scriptUrl) {
            try {
                yield loadScriptFileAsync(scriptUrl, moduleId);
                return lookupComponent(widget.id);
            }
            catch (error) {
                console.error('Failed to load widget script:', error);
                return null;
            }
        }
        return null;
    });
}
/**
 * Validate if widget is from an add-on and check its status
 * Note: This function needs to be called within a component context where useAddOns is available
 *
 * @param widget - Widget to validate
 * @param addOns - AddOns instance from useAddOns hook
 * @returns Add-on validation result
 */
export function validateWidgetAddOn(widget, addOns) {
    const moduleId = getModuleIdFromWidget(widget);
    const isAddOn = addOns.isAddOn(moduleId);
    if (!isAddOn) {
        return {
            isAddOn: false,
            installed: true,
            configured: true,
            needsConfiguration: false
        };
    }
    const { installed, configured } = addOns.isAddOnInstalledAndConfigured(moduleId);
    const needsConfiguration = addOns.isConfigurationRequired(moduleId);
    const addOnName = addOns.getAddOnName(widget, moduleId);
    return {
        isAddOn: true,
        installed,
        configured,
        needsConfiguration,
        addOnName
    };
}
/**
 * Get the icon/thumbnail for a widget
 *
 * @param widget - Widget to get icon for
 * @param addOns - AddOns instance from useAddOns hook (optional, for add-on icons)
 * @returns Icon URL or default icon
 */
export function getWidgetIcon(widget, addOns) {
    // If widget has explicit icon, use it
    if (hasValue(widget.icon)) {
        return widget.icon;
    }
    // Check if it's an add-on widget
    if (addOns) {
        const moduleId = getModuleIdFromWidget(widget);
        const isAddOn = addOns.isAddOn(moduleId);
        if (isAddOn) {
            return addOns.getThunmbNailImagePath(moduleId);
        }
    }
    // Default icon
    return 'fal chart-sine';
}
/**
 * Check if a widget is already added to the dashboard
 *
 * @param widget - Widget to check
 * @param existingWidgets - Currently existing widgets
 * @returns True if widget is already added
 */
export function isWidgetAdded(widget, existingWidgets) {
    const nid = normalizeComponentId(widget.id);
    return existingWidgets.some((w) => normalizeComponentId(w.id) === nid && w.name === widget.name);
}
/**
 * Check if a widget has been removed/trashed
 *
 * @param widget - Widget to check
 * @param trashedWidgets - List of trashed widgets
 * @returns True if widget is in trash
 */
export function isWidgetTrashed(widget, trashedWidgets) {
    const nid = normalizeComponentId(widget.id);
    return trashedWidgets.some((w) => normalizeComponentId(w.id) === nid && w.name === widget.name);
}
/**
 * Get the status of a widget
 *
 * @param widget - Widget to check
 * @param existingWidgets - Currently existing widgets on dashboard
 * @param addOns - AddOns instance from useAddOns hook
 * @returns Status of the widget: 'added' | 'installed' | 'not-installed'
 */
export function getWidgetStatus(widget, existingWidgets, addOns) {
    // Check if already added to dashboard
    if (isWidgetAdded(widget, existingWidgets)) {
        return 'added';
    }
    // Check add-on status
    const addOnValidation = validateWidgetAddOn(widget, addOns);
    if (addOnValidation.isAddOn) {
        return addOnValidation.installed ? 'installed' : 'not-installed';
    }
    // Non-add-on widgets are always "installed"
    return 'installed';
}
/**
 * Filter widgets by status
 *
 * @param widgets - Widgets to filter
 * @param status - Status to filter by
 * @param existingWidgets - Currently existing widgets on dashboard
 * @param addOns - AddOns instance from useAddOns hook
 * @returns Filtered array of widgets
 */
export function filterByStatus(widgets, status, existingWidgets, addOns) {
    if (status === 'all')
        return widgets;
    return widgets.filter(w => {
        const widgetStatus = getWidgetStatus(w, existingWidgets, addOns);
        return widgetStatus === status;
    });
}
